import React from 'react';
import { X, Filter } from 'lucide-react';
import { MultiSelect, Option } from '../../../components/ui/multi-select';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';

export type PipeFilterPayload = {
  projectNames: string[];
  pipeFilters: Array<{ pipeLength: number; wallThickness: number }>;
  pageSize: number;
  page: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  materialCategory?: string;
  pipeFitterOnly?: boolean;
};

interface PipeDashboardFiltersProps {
  projectNames: string[];
  uniquePipeSizes: number[];
  uniqueWallThickness: number[];
  pipeFilters: Array<{ pipeLength: number; wallThickness: number }>;
  selectedProjectNames: string[];
  selectedPipeSizes: string[];
  selectedWallThickness: string[];
  pipeFitterOnly: boolean;
  onProjectNamesChange: (names: string[]) => void;
  onPipeSizesChange: (sizes: string[]) => void;
  onWallThicknessChange: (values: string[]) => void;
  onPipeFiltersChange: (filters: Array<{ pipeLength: number; wallThickness: number }>) => void;
  onPipeFitterOnlyChange: (value: boolean) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  filteredCount?: number;
  hasActiveFilters: boolean;
}

export const PipeDashboardFilters: React.FC<PipeDashboardFiltersProps> = ({
  projectNames,
  uniquePipeSizes,
  uniqueWallThickness,
  pipeFilters,
  selectedProjectNames,
  selectedPipeSizes,
  selectedWallThickness,
  pipeFitterOnly,
  onProjectNamesChange,
  onPipeSizesChange,
  onWallThicknessChange,
  onPipeFiltersChange,
  onPipeFitterOnlyChange,
  onApplyFilters,
  onResetFilters,
  filteredCount = 0,
  hasActiveFilters,
}) => {
  const projectOptions: Option[] = projectNames.map((n) => ({ value: n, label: n }));
  const pipeSizeOptions: Option[] = uniquePipeSizes.map((s) => ({ value: String(s), label: String(s) }));
  const wallOptions: Option[] = uniqueWallThickness.map((w) => ({ value: String(w), label: String(w) }));

  // Combo: unique Size × Wall Thickness combinations (cross product, no duplicates)
  const comboOptions: Option[] = [];
  uniquePipeSizes.forEach((s) => {
    uniqueWallThickness.forEach((w) => {
      comboOptions.push({ value: `${s} × ${w}`, label: `${s} × ${w}` });
    });
  });

  const selectedComboSizes = pipeFilters.map((pf) => `${pf.pipeLength} × ${pf.wallThickness}`);
  const handleComboChange = (selected: string[]) => {
    const newFilters = selected.map((s) => {
      const [pipeLength, wallThickness] = s.split(' × ').map(Number);
      return { pipeLength, wallThickness };
    });
    onPipeFiltersChange(newFilters);
  };

  const handleRemoveCombo = (index: number) => {
    onPipeFiltersChange(pipeFilters.filter((_, i) => i !== index));
  };

  return (
    <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4 h-8">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="font-medium">Filters</span>
          {filteredCount > 0 && (
            <span className="text-sm text-gray-500 ml-2">({filteredCount} materials)</span>
          )}
        </div>
        <div className="h-8 flex items-center gap-2">
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={onResetFilters} className="text-gray-500 hover:text-gray-700">
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
          <Button size="sm" onClick={onApplyFilters} className="bg-[#e04530] hover:bg-[#e04530]/90">
            Apply
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="w-[250px]">
          <MultiSelect
            options={projectOptions}
            selected={selectedProjectNames}
            onChange={onProjectNamesChange}
            placeholder="Project Name"
            className="w-full"
          />
        </div>
        <div className="w-[200px]">
          <MultiSelect
            options={pipeSizeOptions}
            selected={selectedPipeSizes}
            onChange={onPipeSizesChange}
            placeholder="Pipe Size"
            className="w-full"
          />
        </div>
        <div className="w-[200px]">
          <MultiSelect
            options={wallOptions}
            selected={selectedWallThickness}
            onChange={onWallThicknessChange}
            placeholder="Wall Thickness"
            className="w-full"
          />
        </div>

       

        {pipeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {pipeFilters.map((pf, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs"
              >
                {pf.pipeLength} × {pf.wallThickness}
                <button
                  type="button"
                  onClick={() => handleRemoveCombo(i)}
                  className="text-gray-500 hover:text-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        
      </div>
    </div>
  );
};
